import React, { useEffect, useState } from "react";
import AdminShell from "@/components/AdminShell";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { adminAuth } from "@/lib/api";
import { Pin, Lock, Flag, Trash2, Loader2, MessageSquare, MessageCircle, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/university/community`;
const auth = () => ({ Authorization: `Bearer ${adminAuth.getToken()}` });

export default function AdminCommunity() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("posts");
  const [summary, setSummary] = useState(null);
  const [posts, setPosts] = useState([]);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");  // all | flagged | removed

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "flagged" ? { flagged: true } : filter === "removed" ? { removed: true } : {};
      const [s, p, r] = await Promise.all([
        axios.get(`${API}/admin/summary`, { headers: auth() }),
        axios.get(`${API}/admin/posts/list`, { headers: auth(), params }),
        axios.get(`${API}/admin/replies/list`, { headers: auth(), params }),
      ]);
      setSummary(s.data); setPosts(p.data?.items || []); setReplies(r.data?.items || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login?next=/admin/community");
      else toast.error("Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const moderate = async (kind, id, patch) => {
    try {
      await axios.post(`${API}/admin/${kind}/${id}/moderate`, patch, { headers: auth() });
      toast.success("Updated"); load();
    } catch { toast.error("Action failed"); }
  };

  return (
    <AdminShell door="community" title="Moderation">
      <div data-testid="admin-community-page">
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Tile icon={MessageSquare} label="Posts" value={summary.posts} sub={`${summary.posts_removed} removed`} />
            <Tile icon={MessageCircle} label="Replies" value={summary.replies} sub={`${summary.replies_removed} removed`} />
            <Tile icon={Flag} label="Flagged Posts" value={summary.flagged_posts} />
            <Tile icon={AlertTriangle} label="Flagged Replies" value={summary.flagged_replies} />
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-2">
            {[["posts", "Posts"], ["replies", "Replies"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`comm-tab-${k}`} className={`text-sm py-2 px-3 ${tab === k ? "border-b-2 border-purple-900 text-purple-900 font-medium" : "text-slate-500"}`}>{l}</button>
            ))}
          </div>
          <div className="flex gap-2">
            {[["all", "All"], ["flagged", "Flagged"], ["removed", "Removed"]].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`comm-filter-${k}`} className={`rounded-full h-8 px-3 text-xs ${filter === k ? "bg-purple-900 text-white" : "bg-white border border-slate-200 text-slate-700"}`}>{l}</button>
            ))}
          </div>
        </div>

        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <div className="rounded-2xl bg-white border border-slate-200 divide-y divide-slate-100">
            {(tab === "posts" ? posts : replies).length === 0 && <div className="p-6 text-center text-slate-400 text-sm">No items match.</div>}
            {(tab === "posts" ? posts : replies).map((item) => (
              <div key={item.id} className={`p-4 ${item.is_removed ? "opacity-50" : ""}`} data-testid={`comm-${tab}-${item.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-500">{item.author_email || item.user_email} · {(item.created_at || "").slice(0, 16).replace("T", " ")}{item.scope && ` · ${item.scope}/${item.scope_id || ""}`}</div>
                    {item.title && <div className="font-display text-sm mt-0.5">{item.title}</div>}
                    <div className="text-sm text-slate-700 mt-1 line-clamp-3">{item.body || item.content}</div>
                    <div className="mt-2 flex gap-2 text-[10px] uppercase tracking-wide">
                      {item.is_pinned && <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">Pinned</span>}
                      {item.is_locked && <span className="bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full">Locked</span>}
                      {item.is_flagged && <span className="bg-rose-100 text-rose-800 px-2 py-0.5 rounded-full">Flagged</span>}
                      {item.is_removed && <span className="bg-rose-200 text-rose-900 px-2 py-0.5 rounded-full">Removed</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {tab === "posts" && (
                      <>
                        <button onClick={() => moderate("posts", item.id, { pin: !item.is_pinned })} className={item.is_pinned ? "text-amber-600" : "text-slate-400"} title="Pin"><Pin className="w-4 h-4" /></button>
                        <button onClick={() => moderate("posts", item.id, { lock: !item.is_locked })} className={item.is_locked ? "text-slate-700" : "text-slate-400"} title="Lock"><Lock className="w-4 h-4" /></button>
                      </>
                    )}
                    <button onClick={() => moderate(tab, item.id, { remove: !item.is_removed })} className={item.is_removed ? "text-emerald-600" : "text-rose-600"} title="Toggle remove" data-testid={`comm-remove-${item.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminShell>
  );
}

function Tile({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500"><Icon className="w-3.5 h-3.5" />{label}</div>
      <div className="text-2xl font-display text-slate-900 mt-1">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
